import React, { useState } from 'react';
import { amenityApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import {
  MdComputer, MdTv, MdPhone, MdWifi, MdAir, MdChair,
  MdMic, MdVideocam, MdCable, MdOutlet, MdScreenShare,
  MdSlideshow, MdMonitor, MdDevices, MdRouter,
  MdLightbulb, MdTableRestaurant, MdLocalCafe, MdPrint,
  MdDesktopWindows, MdLaptop, MdKeyboard, MdMouse, MdHeadset
} from 'react-icons/md';
import './AddRoomModal.css';

function AddAmenityModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'MdComputer'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const commonIcons = [
    { name: 'MdComputer', icon: MdComputer, label: 'Computer' },
    { name: 'MdTv', icon: MdTv, label: 'TV' },
    { name: 'MdSlideshow', icon: MdSlideshow, label: 'Projector' },
    { name: 'MdScreenShare', icon: MdScreenShare, label: 'Screen Share' },
    { name: 'MdMonitor', icon: MdMonitor, label: 'Monitor' },
    { name: 'MdDesktopWindows', icon: MdDesktopWindows, label: 'Desktop' },
    { name: 'MdLaptop', icon: MdLaptop, label: 'Laptop' },
    { name: 'MdPhone', icon: MdPhone, label: 'Phone' },
    { name: 'MdWifi', icon: MdWifi, label: 'WiFi' },
    { name: 'MdRouter', icon: MdRouter, label: 'Router' },
    { name: 'MdAir', icon: MdAir, label: 'AC' },
    { name: 'MdChair', icon: MdChair, label: 'Chair' },
    { name: 'MdTableRestaurant', icon: MdTableRestaurant, label: 'Table' },
    { name: 'MdMic', icon: MdMic, label: 'Microphone' },
    { name: 'MdVideocam', icon: MdVideocam, label: 'Camera' },
    { name: 'MdHeadset', icon: MdHeadset, label: 'Headset' },
    { name: 'MdCable', icon: MdCable, label: 'Cable' },
    { name: 'MdOutlet', icon: MdOutlet, label: 'Outlet' },
    { name: 'MdLightbulb', icon: MdLightbulb, label: 'Light' },
    { name: 'MdLocalCafe', icon: MdLocalCafe, label: 'Coffee' },
    { name: 'MdPrint', icon: MdPrint, label: 'Printer' },
    { name: 'MdKeyboard', icon: MdKeyboard, label: 'Keyboard' },
    { name: 'MdMouse', icon: MdMouse, label: 'Mouse' },
    { name: 'MdDevices', icon: MdDevices, label: 'Devices' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Amenity name is required';
    }

    if (!formData.icon.trim()) {
      newErrors.icon = 'Icon is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await amenityApi.add({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        icon: formData.icon.trim(),
      });

      toast.success('Amenity added successfully!', {
        position: "top-right",
        autoClose: 3000,
      });

      resetForm();
      onSuccess && onSuccess();
      onClose();

    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data || error.message || 'Failed to add amenity';
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'MdComputer'
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-room-modal-overlay" onClick={handleClose}>
      <div className="add-room-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="add-room-modal-header">
          <h2>Add Amenity</h2>
          <button className="add-room-modal-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="add-room-form">
          {/* AMENITY NAME */}
          <div className="form-group">
            <label htmlFor="name">
              Amenity Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Projector, Whiteboard, WiFi"
              className={errors.name ? 'error' : ''}
              autoFocus
            />
            {errors.name && (
              <span className="error-message">{errors.name}</span>
            )}
          </div>

          {/* DESCRIPTION */}
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of the amenity"
              rows="3"
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>

          {/* ICON SELECTION */}
          <div className="form-group">
            <label>
              Icon <span className="required">*</span>
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: '8px',
              marginTop: '8px'
            }}>
              {commonIcons.map(({ name, icon: IconComponent, label }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleChange('icon', name)}
                  style={{
                    fontSize: '24px',
                    padding: '12px',
                    border: formData.icon === name ? '2px solid #4CAF50' : '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: formData.icon === name ? '#e8f5e9' : 'white',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={label}
                >
                  <IconComponent />
                </button>
              ))}
            </div>
            {errors.icon && (
              <span className="error-message">{errors.icon}</span>
            )}
            <p className="helper-text" style={{ marginTop: '8px', fontSize: '12px' }}>
              Select an icon for the amenity
            </p>
          </div>

          {/* INFO MESSAGE */}
          <div className="info-message">
            <span>Once added, this amenity will be available for conference rooms.</span>
          </div>

          {/* FORM ACTIONS */}
          <div className="add-room-form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Amenity'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddAmenityModal;
